import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Credentials, Solver, type Challenge, type Entered } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Field, Form, Loading } from '@beyond-js/ui/react';
import { Problem, useCountdown } from '@accounts/ui';
import { explain } from './explain';

const valid = (email: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

/**
 * Signing in with an address and a password, and asking for a recovery message.
 *
 * It answers each thing the service says in the person's terms (ACC-01, ACC-02, ACC-11): an
 * unconfirmed address offers **Send the link again** with the wait shown; repeated failures solve the
 * service's puzzle in this browser and try once more; a wait is said in seconds; a recovery message,
 * once sent, can be asked for again after the wait. A password is never kept, whatever happened. Nothing
 * still to write is said (D183): while a field is empty the form's action waits quietly and a press puts the
 * caret there; an address that is not one is said in its field.
 *
 * @param back The confirmation and recovery addresses a message leads back to
 * @param onEntered What to do with a session or a second-factor step
 */
export function EmailForm({ initial, hinted, back, onEntered }: { initial: string; hinted: boolean; back: { confirm: string; reset: string }; onEntered: (entered: Entered) => Promise<void> }) {
	const { text, date, client } = usePresence();
	const credentials = useMemo(() => new Credentials(client), [client]);

	const [email, setEmail] = useState(initial);
	const [password, setPassword] = useState('');
	const [asking, setAsking] = useState(false);
	const [busy, setBusy] = useState(false);
	const [checking, setChecking] = useState(false);
	const [failure, setFailure] = useState<{ words: string; error: unknown }>();
	const [notice, setNotice] = useState<string>();
	const [unconfirmed, setUnconfirmed] = useState(false);
	const [invalid, setInvalid] = useState<{ email?: string }>({});
	const [waiting, wait] = useCountdown();

	function fail(error: unknown) {
		setFailure({ words: explain(error, text, date), error });
		if (Refusal.is(error) && error.retry) wait(error.retry);
	}

	/** One attempt, and once more with the puzzle solved when the service asks for one */
	async function attempt(challenge?: Challenge): Promise<void> {
		try {
			const solved = challenge ? await new Solver().solve(challenge) : undefined;
			setChecking(false);
			const entered = await credentials.signin(email, password, solved);
			setPassword('');
			await onEntered(entered);
		} catch (error) {
			if (Refusal.is(error) && error.code === 'CHALLENGE_REQUIRED' && !challenge) {
				setChecking(true);
				return attempt(error.details?.challenge as Challenge);
			}
			throw error;
		}
	}

	// Both fields hold something here: an empty one stopped the submission in `Form`, saying nothing
	async function submit() {
		const problems: { email?: string } = {};
		if (!valid(email)) problems.email = text('error.field.email');
		setInvalid(problems);
		if (Object.keys(problems).length) return;
		setBusy(true);
		setFailure(undefined);
		setNotice(undefined);
		setUnconfirmed(false);
		try {
			await attempt();
		} catch (error) {
			setPassword('');
			if (Refusal.is(error) && error.code === 'ADDRESS_UNCONFIRMED') setUnconfirmed(true);
			else fail(error);
		} finally {
			setBusy(false);
			setChecking(false);
		}
	}

	/** Sends the confirmation message again (ACC-01) */
	async function resend() {
		setBusy(true);
		setFailure(undefined);
		try {
			const sent = await credentials.resend(email, back.confirm);
			setNotice(text('signin.resend.sent', { email }));
			wait(sent.retry);
		} catch (error) {
			fail(error);
		} finally {
			setBusy(false);
		}
	}

	/** Asks for a recovery message. The same answer whether or not the address has an account */
	async function forgot() {
		if (!valid(email)) {
			setInvalid({ email: text('error.field.email') });
			return;
		}
		setInvalid({});
		setBusy(true);
		setFailure(undefined);
		try {
			const sent = await credentials.recover(email, back.reset);
			setNotice(text('signin.recover.sent'));
			wait(sent.retry);
		} catch (error) {
			fail(error);
		} finally {
			setBusy(false);
		}
	}

	const later = waiting > 0 ? text(asking ? 'signin.recover.wait' : 'signin.resend.wait', { count: waiting }) : undefined;

	return (
		<Form
			className="flex flex-col gap-4"
			onSubmit={event => {
				event.preventDefault();
				void (asking ? forgot() : submit());
			}}
		>
			{failure ? <Problem error={failure.error} title={failure.words} /> : null}
			{notice ? <Callout tone="success" live title={notice} body={later} /> : null}
			{unconfirmed ? (
				<Callout
					tone="warning"
					live
					title={text('signin.unconfirmed', { email })}
					body={waiting > 0 && !notice ? later : undefined}
					actions={
						<Button type="button" variant="secondary" busy={busy} disabled={waiting > 0} onClick={() => void resend()}>
							{text('signin.resend')}
						</Button>
					}
				/>
			) : null}
			{checking ? <Loading label={text('signin.challenge')} /> : null}

			<Field label={text('signin.email')} error={invalid.email}>
				<input type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} />
			</Field>
			{asking ? (
				<>
					<p className="text-body text-ink-muted">{text('signin.recover.lead')}</p>
					<Button type="submit" variant="primary" busy={busy} disabled={waiting > 0} labels={{ busy: text('signin.working') }}>
						{text('signin.recover.submit')}
					</Button>
					<Button type="button" variant="quiet" onClick={() => setAsking(false)} disabled={busy}>
						{text('action.back')}
					</Button>
				</>
			) : (
				<>
					<Field label={text('signin.password')}>
						<input type="password" autoComplete="current-password" autoFocus={hinted} required value={password} onChange={event => setPassword(event.target.value)} />
					</Field>
					<Button type="submit" variant="primary" busy={busy} disabled={!unconfirmed && waiting > 0} labels={{ busy: text('signin.working') }}>
						{text('signin.submit')}
					</Button>
					<Button type="button" variant="quiet" onClick={() => setAsking(true)} disabled={busy}>
						{text('signin.recover')}
					</Button>
					<p className="text-center text-body text-ink-muted">
						<Link className="underline" to={`/signup${globalThis.location.search}`}>
							{text('signin.create')}
						</Link>
					</p>
				</>
			)}
		</Form>
	);
}
